// ═══════════════════════════════════════════════════════════════
// Paso 4: Resultado de la importación
// ═══════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { CheckCircle2, XCircle, Users, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';

const Step4_Summary = ({ resultado, resumen, onReset, onClose }) => {
  const [mostrarFallidos, setMostrarFallidos] = useState(true);

  const importados     = resultado?.importados ?? 0;
  const fallidos       = resultado?.fallidos ?? 0;
  const totalIntentados = resultado?.total_intentados ?? 0;
  const erroresDetalle = resultado?.errores_detalle ?? [];
  const exito = importados > 0;

  return (
    <div className="space-y-6">
      {/* Header resultado */}
      <div className="text-center">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
          exito ? 'bg-green-100' : 'bg-red-100'
        }`}>
          {exito
            ? <CheckCircle2 className="w-8 h-8 text-green-600" />
            : <XCircle className="w-8 h-8 text-red-600" />
          }
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {exito ? 'Importación completada' : 'Importación sin resultados'}
        </h3>
        <p className="text-sm text-gray-500">
          {exito
            ? `Se importaron ${importados} empleado${importados !== 1 ? 's' : ''} exitosamente.`
            : 'No se pudo importar ningún empleado.'}
        </p>
      </div>

      {/* Tarjetas de stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-700">{importados}</p>
          <p className="text-xs text-green-600 mt-1">Importados</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-red-700">{fallidos}</p>
          <p className="text-xs text-red-600 mt-1">Con errores</p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-gray-700">{totalIntentados}</p>
          <p className="text-xs text-gray-500 mt-1">Total intentados</p>
        </div>
      </div>

      {/* Resumen del archivo original */}
      {resumen && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-xs font-medium text-blue-800 mb-2">Resumen del archivo procesado:</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-blue-700">
            <span>Filas totales en archivo:</span>
            <span className="font-medium">{resumen.total_filas}</span>
            <span>Filas válidas (sin errores):</span>
            <span className="font-medium">{resumen.total_validos}</span>
            <span>Filas con advertencias:</span>
            <span className="font-medium">{resumen.total_warnings}</span>
            <span>Filas con errores críticos:</span>
            <span className="font-medium">{resumen.total_errores}</span>
          </div>
        </div>
      )}

      {/* Detalle de filas fallidas */}
      {fallidos > 0 && (
        <div className="border border-red-200 rounded-lg overflow-hidden">
          {/* Cabecera colapsable */}
          <button
            onClick={() => setMostrarFallidos(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 bg-red-50 text-left hover:bg-red-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-500" />
              <span className="text-sm font-medium text-red-800">
                {fallidos} fila{fallidos !== 1 ? 's' : ''} no se pudo{fallidos !== 1 ? 'ron' : ''} importar
              </span>
              <span className="text-xs text-red-600">— corrige el archivo y vuelve a importarlas</span>
            </div>
            {mostrarFallidos
              ? <ChevronUp className="w-4 h-4 text-red-400" />
              : <ChevronDown className="w-4 h-4 text-red-400" />
            }
          </button>

          {/* Lista de filas fallidas */}
          {mostrarFallidos && (
            <div className="divide-y divide-red-100 max-h-48 overflow-y-auto">
              {erroresDetalle.map((e, i) => (
                <div key={i} className="flex items-start gap-3 px-4 py-2.5 bg-white">
                  <span className="text-xs font-mono bg-red-100 text-red-700 px-2 py-0.5 rounded flex-shrink-0 mt-0.5">
                    Fila {e.fila}
                  </span>
                  <span className="text-xs text-gray-700">{e.error}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Acciones */}
      <div className="flex justify-between pt-2">
        <button
          onClick={onReset}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Nueva importación
        </button>

        <button
          onClick={onClose}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Users className="w-4 h-4" />
          Ver empleados
        </button>
      </div>
    </div>
  );
};

export default Step4_Summary;
